// Audit Log Utility for tracking data changes
const AUDIT_LOG_KEY = 'bme-audit-logs';

// Get all audit logs
export const getAuditLogs = () => {
  try {
    const logs = localStorage.getItem(AUDIT_LOG_KEY);
    return logs ? JSON.parse(logs) : [];
  } catch (error) {
    console.error('Error loading audit logs:', error);
    return [];
  }
};

// Save audit logs
const saveAuditLogs = (logs) => {
  try {
    localStorage.setItem(AUDIT_LOG_KEY, JSON.stringify(logs));
    return true;
  } catch (error) {
    console.error('Error saving audit logs:', error);
    return false;
  }
};

// Add a new audit log entry
export const addAuditLog = (action, entityType, entityId, details, user) => {
  const logs = getAuditLogs();
  
  const newLog = {
    id: Date.now().toString(36) + Math.random().toString(36).substr(2),
    timestamp: new Date().toISOString(),
    action, // 'CREATE', 'UPDATE', 'DELETE', 'UPLOAD', 'DOWNLOAD'
    entityType, // 'CALL_LOG', 'ASSET', 'TRAINING', 'CONTRACT', 'PM_CAL', 'REPORT'
    entityId,
    details: details || '',
    user: user ? {
      id: user.id,
      username: user.username,
      name: user.name || user.username,
      role: user.role
    } : null
  };
  
  // Add to beginning (newest first)
  logs.unshift(newLog);
  
  // Keep only last 1000 logs to prevent storage issues
  if (logs.length > 1000) {
    logs.pop();
  }
  
  saveAuditLogs(logs);
  
  // Also log to console for debugging
  console.log('📝 Audit Log:', newLog);
  
  return newLog;
};

// Log data deletion
export const logDeletion = (entityType, entityId, details, user) => {
  return addAuditLog('DELETE', entityType, entityId, details, user);
};

// Log data creation
export const logCreation = (entityType, entityId, details, user) => {
  return addAuditLog('CREATE', entityType, entityId, details, user);
};

// Log data update
export const logUpdate = (entityType, entityId, details, user) => {
  return addAuditLog('UPDATE', entityType, entityId, details, user);
};

// Log file upload
export const logUpload = (entityType, entityId, details, user) => {
  return addAuditLog('UPLOAD', entityType, entityId, details, user);
};

// Log data export/download
export const logDownload = (entityType, details, user) => {
  return addAuditLog('DOWNLOAD', entityType, null, details, user);
};

// Get logs filtered by criteria
export const getFilteredLogs = (filters = {}) => {
  let logs = getAuditLogs();
  
  if (filters.action) {
    logs = logs.filter(log => log.action === filters.action);
  }
  
  if (filters.entityType) {
    logs = logs.filter(log => log.entityType === filters.entityType);
  }
  
  if (filters.userId) {
    logs = logs.filter(log => log.user && log.user.id === filters.userId);
  }
  
  if (filters.startDate) {
    const start = new Date(filters.startDate);
    logs = logs.filter(log => new Date(log.timestamp) >= start);
  }
  
  if (filters.endDate) {
    const end = new Date(filters.endDate);
    logs = logs.filter(log => new Date(log.timestamp) <= end);
  }
  
  if (filters.searchTerm) {
    const term = filters.searchTerm.toLowerCase();
    logs = logs.filter(log => 
      (log.details && log.details.toLowerCase().includes(term)) ||
      (log.entityId && log.entityId.toLowerCase().includes(term)) ||
      (log.user && log.user.username && log.user.username.toLowerCase().includes(term)) ||
      (log.user && log.user.name && log.user.name.toLowerCase().includes(term))
    );
  }
  
  return logs;
};

// Clear all audit logs (admin only)
export const clearAuditLogs = () => {
  localStorage.removeItem(AUDIT_LOG_KEY);
  return true;
};

// Export logs to Excel format
export const exportLogsToExcel = (logs) => {
  return logs.map(log => ({
    'Date & Time': new Date(log.timestamp).toLocaleString(),
    'Action': log.action,
    'Entity Type': log.entityType,
    'Entity ID': log.entityId || 'N/A',
    'Details': log.details,
    'User Name': log.user ? log.user.name : 'Unknown',
    'Username': log.user ? log.user.username : 'Unknown',
    'User Role': log.user ? log.user.role : 'Unknown'
  }));
};

// Get action color for UI
export const getActionColor = (action) => {
  const colors = {
    'CREATE': '#22c55e', // Green
    'UPDATE': '#3b82f6', // Blue
    'DELETE': '#dc2626', // Red
    'UPLOAD': '#f59e0b', // Orange
    'DOWNLOAD': '#8b5cf6' // Purple
  };
  return colors[action] || '#6b7280';
};

// Get action icon for UI
export const getActionIcon = (action) => {
  const icons = {
    'CREATE': '➕',
    'UPDATE': '✏️',
    'DELETE': '🗑️',
    'UPLOAD': '📤',
    'DOWNLOAD': '📥'
  };
  return icons[action] || '📝';
};
